import React, { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { Toaster } from 'sonner';
import lazyWithRetry from './utils/lazyWithRetry';
import { CHATGPT_CONNECT_RETURN_KEY } from './utils/sessionKeys';
import { BORIS_ENABLED } from './utils/borisFlag';
import { installEscapeHandler } from './utils/escapeClosesPopups';

// ---------------------------------------------------------------------------
// WP-SPLIT — load only the screen you open.
//
// Everything used to be imported here, so opening the login page downloaded the
// whole program (3.5 MB) before the password box appeared — including the
// stocktake, payroll and the customer portal, for a customer who only ever uses
// the portal. Now each screen is its own file, fetched the moment somebody opens
// it and then cached by the browser and the service worker.
//
// Eager (below) = small, and needed before anybody has chosen anything: the
// login box, the update banner, the error pages, the public clock-in kiosk and
// the two-factor prompt. Everything else is lazy.
//
// lazyWithRetry, not React.lazy: see utils/lazyWithRetry.js — a page left open
// across a deploy asks for a file name that no longer exists, and it must
// reload rather than land on the error page.
// ---------------------------------------------------------------------------

// --- eager: needed before the first click ---
import Login from './components/Login';
import DesktopUpdateBanner from './components/DesktopUpdateBanner';
import ClockInKiosk from './components/ClockInKiosk';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import GeneralErrorPage from './components/GeneralErrorPage';
import NetworkErrorPage from './components/NetworkErrorPage';
import TwoFactorEnrolPrompt, { TWOFA_PROMPT_DISMISSED_KEY } from './components/TwoFactorEnrolPrompt';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PortalAuthProvider } from './components/portal/PortalAuth';

// --- on demand: staff screens ---
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const ClientManagement = lazyWithRetry(() => import('./components/ClientManagement'));
const OrderManagement = lazyWithRetry(() => import('./components/OrderManagement'));
const ProductionBoard = lazyWithRetry(() => import('./components/ProductionBoard'));
const Reports = lazyWithRetry(() => import('./components/Reports'));
const MaterialsManagement = lazyWithRetry(() => import('./components/MaterialsManagement'));
const SuppliersManagement = lazyWithRetry(() => import('./components/SuppliersManagement'));
const ProductSpecifications = lazyWithRetry(() => import('./components/ProductSpecifications'));
const MachineryRates = lazyWithRetry(() => import('./components/MachineryRates'));
const Calculators = lazyWithRetry(() => import('./components/Calculators'));
const Stocktake = lazyWithRetry(() => import('./components/Stocktake'));
const Designer = lazyWithRetry(() => import('./components/Designer'));
const PayrollManagement = lazyWithRetry(() => import('./components/PayrollManagement'));
const TaskManagement = lazyWithRetry(() => import('./components/TaskManagement'));
const Invoicing = lazyWithRetry(() => import('./components/Invoicing'));
const StaffSecurity = lazyWithRetry(() => import('./components/StaffSecurity'));
const PrinterConnection = lazyWithRetry(() => import('./components/PrinterConnection'));
const Documentation = lazyWithRetry(() => import('./components/Documentation'));
const XeroCallback = lazyWithRetry(() => import('./components/XeroCallback'));
const XeroSettings = lazyWithRetry(() => import('./components/XeroSettings'));
const ChatGPTIntegration = lazyWithRetry(() => import('./components/ChatGPTIntegration'));
const ChatGPTConsent = lazyWithRetry(() => import('./components/ChatGPTConsent'));
const ArchivedJobs = lazyWithRetry(() => import('./components/ArchivedJobs'));
const ErrorLogViewer = lazyWithRetry(() => import('./components/ErrorLogViewer'));
const IssuesLogViewer = lazyWithRetry(() => import('./components/IssuesLogViewer'));
const ClientProductCatalogue = lazyWithRetry(() => import('./components/ClientProductCatalogue'));
const QuickQuotes = lazyWithRetry(() => import('./components/QuickQuotes'));
const BackupManagement = lazyWithRetry(() => import('./components/BackupManagement'));
const EmailReviewQueue = lazyWithRetry(() => import('./components/EmailReviewQueue'));
const EmailExclusions = lazyWithRetry(() => import('./components/EmailExclusions'));
const AuthDiagnostics = lazyWithRetry(() => import('./components/AuthDiagnostics'));
const BorisAssistant = lazyWithRetry(() => import('./components/BorisAssistant'));

// --- on demand: customer portal (never pulled by a staff screen, and vice versa) ---
const PortalLogin = lazyWithRetry(() => import('./components/portal/PortalLogin'));
const PortalDashboard = lazyWithRetry(() => import('./components/portal/PortalDashboard'));
const PortalTwoFactorSetup = lazyWithRetry(() => import('./components/portal/PortalTwoFactorSetup'));

// The one thing a person sees while a screen is being fetched. Same dark shell
// and yellow spinner as the "checking your session" screen below, so it reads as
// the app thinking rather than as a blank page.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      <p className="text-gray-400 text-sm">Loading…</p>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const routeLocation = useLocation();

  // WP-GPT — the consent page must survive logging in.
  //
  // Normally it already does: when nobody is signed in App.js renders <Login />
  // in place of the routes WITHOUT navigating, so the address bar still says
  // /chatgpt-connect?request_id=… and the consent page mounts as soon as the
  // login succeeds. The one hole is a stale session being dropped while the
  // page is open — utils/api.js hard-navigates to "/" on a dead token, which
  // throws the request_id away. The consent page parks its address in
  // sessionStorage, so this brings the person straight back to it.
  useEffect(() => {
    if (!user) return;
    if (routeLocation.pathname.startsWith('/chatgpt-connect')) return;
    let parked = null;
    try {
      parked = sessionStorage.getItem(CHATGPT_CONNECT_RETURN_KEY);
    } catch (e) {
      return; // private browsing — nothing was parked
    }
    if (!parked || !parked.startsWith('/chatgpt-connect')) return;
    // Never bounce a page back to where it already is — that is a loop, not a
    // return trip. The key is removed whatever happens next, so one dropped
    // session buys exactly one trip back and never a permanent redirect.
    const here = `${routeLocation.pathname}${routeLocation.search}`;
    try { sessionStorage.removeItem(CHATGPT_CONNECT_RETURN_KEY); } catch (e) { /* ignore */ }
    if (parked === here) return;
    navigate(parked, { replace: true });
  }, [user, routeLocation.pathname, routeLocation.search, navigate]);

  // WP-2E — everything the prompt covers lives inside this element, so it can be
  // switched off properly while the prompt is up (see the effect below).
  const appAreaRef = useRef(null);
  // WP-2E — the prompt closes itself through this, not through its own state.
  // One value drives both the box on screen and the "inert" switch below, so the
  // app can never be left switched off with no prompt on top of it (which used
  // to happen whenever the re-read of the user failed after Not now or Done).
  const [promptClosed, setPromptClosed] = useState(false);

  // A new person on this computer gets the prompt back. Switching 2FA back off
  // (My account → Two-factor authentication → Disable) must also bring it back:
  // without this, someone held on compulsory set-up could finish it, switch it
  // straight off again, and never see the prompt again for the rest of the
  // session. A "Not now" is still remembered, because that is held by the
  // sessionStorage flag and does not change `totp_enabled`.
  useEffect(() => {
    setPromptClosed(false);
  }, [user?.id, user?.totp_enabled]);

  // WP-2E — ask for two-factor authentication at login. The routes still mount
  // underneath; the prompt is a full-screen cover over them, and while it is up
  // the rest of the app is made inert, so the mouse, the Tab key and screen
  // readers all stop at the prompt until Set up now or Not now is pressed.
  let promptDismissedThisSession = false;
  try {
    promptDismissedThisSession = Boolean(sessionStorage.getItem(TWOFA_PROMPT_DISMISSED_KEY));
  } catch (e) {
    // Private browsing or a locked-down browser: treat it as not dismissed.
  }
  // Compulsory set-up (an admin has just unlocked them) beats the "Not now"
  // flag. That flag belongs to the window, not to the person, so it can have
  // been left behind by whoever used this computer before them.
  const showTwoFactorPrompt = Boolean(
    user
    && user.twofa_setup_required
    && !user.totp_enabled
    && !promptClosed
    && (user.twofa_setup_mandatory || !promptDismissedThisSession)
  );

  useEffect(() => {
    const area = appAreaRef.current;
    if (!area) return;
    if (showTwoFactorPrompt) {
      area.setAttribute('inert', '');
      area.setAttribute('aria-hidden', 'true');
    } else {
      area.removeAttribute('inert');
      area.removeAttribute('aria-hidden');
    }
  }, [showTwoFactorPrompt]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!user) {
    // WP-UPDATE — the banner sits above the login box too. Opening the app and
    // being asked to sign in is the moment a person is most able to quit and
    // reinstall, and /api/desktop-client/info needs no token.
    return (
      <>
        <DesktopUpdateBanner />
        <Login />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {showTwoFactorPrompt && <TwoFactorEnrolPrompt onClose={() => setPromptClosed(true)} />}
      <div ref={appAreaRef}>
      {/* WP-SPLIT — its own boundary, inside the app area, so fetching a screen
          never tears down the two-factor prompt sitting above it. */}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/clients" element={<ClientManagement />} />
        <Route path="/quick-quotes" element={<QuickQuotes />} />
        <Route path="/orders" element={<OrderManagement />} />
        <Route path="/production" element={<ProductionBoard />} />
        <Route path="/invoicing" element={<Invoicing />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/email-review" element={<EmailReviewQueue />} />
        <Route path="/email-exclusions" element={<EmailExclusions />} />
        <Route path="/email-auth-diagnostics" element={<AuthDiagnostics />} />
        <Route path="/products-materials" element={<MaterialsManagement />} />
        <Route path="/suppliers" element={<SuppliersManagement />} />
        <Route path="/product-specifications" element={<ProductSpecifications />} />
        <Route path="/machinery-rates" element={<MachineryRates />} />
        <Route path="/calculators" element={<Calculators />} />
        <Route path="/stocktake" element={<Stocktake />} />
        <Route path="/designer" element={<Designer />} />
        <Route path="/payroll" element={<PayrollManagement />} />
        <Route path="/tasks" element={<TaskManagement />} />
        <Route path="/staff-security" element={<StaffSecurity />} />
        <Route path="/printer-connection" element={<PrinterConnection />} />
        <Route path="/backups" element={<BackupManagement />} />
        <Route path="/xero-settings" element={<XeroSettings />} />
        {/* WP-GPT — ChatGPT connection (admin) and the consent page ChatGPT sends people to. */}
        <Route path="/chatgpt-integration" element={<ChatGPTIntegration />} />
        <Route path="/chatgpt-connect" element={<ChatGPTConsent />} />
        <Route path="/documentation" element={<Documentation />} />
        <Route path="/clients/:clientId/archived-jobs" element={<ArchivedJobs />} />
        <Route path="/clients/:clientId/product-catalogue" element={<ClientProductCatalogue />} />
        <Route path="/xero/callback" element={<XeroCallback />} />
        <Route path="/xero-oauth-callback" element={<XeroSettings />} />
        <Route path="/error-logs" element={<ErrorLogViewer />} />
        <Route path="/issues" element={<IssuesLogViewer />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      {/* Boris only appears after login, when the user clicks "Chat with Boris". */}
      <BorisLauncher />
      </div>
    </div>
  );
}

// "Chat with Boris" button (logged-in app only). In Electron it summons the
// separate always-on-top desktop window (draggable anywhere); in a plain browser
// (no overlay window) it shows the in-window Boris instead.
function BorisLauncher() {
  const [shownInWindow, setShownInWindow] = useState(false);
  const hasOverlay = typeof window !== 'undefined' && window.electron && window.electron.borisOverlay;
  // WP-SPLIT — in a build with Boris switched off, show no button and fetch
  // nothing. The flag comes from utils/borisFlag.js, which is tiny and always
  // loaded; reading it out of BorisAssistant.js would download the whole Boris
  // chunk just to discover he renders nothing.
  if (!BORIS_ENABLED) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => { if (hasOverlay) window.electron.borisOverlay.summon(); else setShownInWindow(true); }}
        className="fixed z-[55] bottom-4 right-4 flex items-center gap-2 rounded-full bg-yellow-400 text-gray-900 shadow-xl px-4 py-2.5 text-sm font-semibold hover:bg-yellow-300"
        title="Chat with Boris"
      >
        <span>💬</span> Chat with Boris
      </button>
      {/* WP-SPLIT — Boris is fetched on the first click. No spinner: he is a
          floating window, not the page, so an empty corner for a moment is the
          right thing to show rather than a full-screen "Loading…". */}
      {!hasOverlay && shownInWindow && (
        <Suspense fallback={null}><BorisAssistant /></Suspense>
      )}
    </>
  );
}

function App() {
  // WP-ESC — Escape closes the popup, everywhere, once.
  //
  // Staff screens and the customer portal are both below this component, so
  // installing the handler here covers the whole program (Q1 a). It presses the
  // popup's own X / Cancel / Close and never saves anything; the rules are
  // written out in utils/escapeClosesPopups.js.
  useEffect(() => installEscapeHandler(), []);

  // Boris desktop-overlay window: a separate transparent always-on-top window
  // loads the app with ?borisOverlay=1 and renders ONLY Boris (no auth, no app).
  const isBorisOverlay = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('borisOverlay') === '1';
  if (isBorisOverlay) {
    return (
      <BrowserRouter>
        {/* WP-SPLIT — no spinner here. This window is transparent, frameless and
            click-through, and it is as big as every monitor put together, so an
            opaque full-screen "Loading…" panel would black out the desktop while the
            Boris chunk is fetched. Showing nothing until he arrives is right. */}
        <Suspense fallback={null}>
          <BorisAssistant overlay />
        </Suspense>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <div className="App">
            {/* WP-SPLIT — one shared fallback for the public pages and the
                customer portal. The staff screens have their own boundary
                inside AppRoutes (see above). */}
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public error pages - accessible without authentication */}
              <Route path="/error" element={<GeneralErrorPage />} />
              <Route path="/network-error" element={<NetworkErrorPage />} />
              {/* Clock-In Kiosk - public, no login required */}
              <Route path="/clock-in" element={<ClockInKiosk />} />
              {/* Customer Portal - separate auth system */}
              <Route path="/customer-portal/login" element={
                <PortalAuthProvider>
                  <PortalLogin />
                </PortalAuthProvider>
              } />
              {/* WP-P2F — compulsory two-factor set-up. It sits above the
                  catch-all so it is reachable with only a 15-minute set-up
                  token, before any portal session exists. */}
              <Route path="/customer-portal/setup-2fa" element={
                <PortalAuthProvider>
                  <PortalTwoFactorSetup />
                </PortalAuthProvider>
              } />
              <Route path="/customer-portal/*" element={
                <PortalAuthProvider>
                  <PortalDashboard />
                </PortalAuthProvider>
              } />
              {/* All other routes require staff authentication */}
              <Route path="/*" element={<AppRoutes />} />
            </Routes>
            </Suspense>
            <PWAInstallPrompt />
            <Toaster
              position="top-right"
              theme="dark"
              visibleToasts={5}
              expand={true}
              closeButton={true}
              duration={10000}
              toastOptions={{
                style: {
                  background: '#1f2937',
                  color: '#f9fafb',
                  border: '1px solid #374151',
                },
              }}
            />
          </div>
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;














